import { z } from 'zod';
import { PlatformSchema, SourceSchema } from './domain.js';
import { ClientBriefSchema } from './postcall.js';
import { TipSchema, TriggerSchema } from './tips.js';
/**
 * WebSocket protocol for `/v1/live` (SPEC §5.2). Every message is JSON with a
 * `type` discriminator and is validated with zod on both ends.
 *
 * Authentication: the access token is passed as the `token` query parameter
 * when opening the socket (browsers and Electron cannot set WS headers easily).
 */
export const SegmentPayloadSchema = z.object({
    /** Monotonic per-session sequence number assigned by the desktop app. */
    seq: z.number().int().nonnegative(),
    source: SourceSchema,
    text: z.string().min(1).max(4000),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
});
export const SessionStartSchema = z.object({
    type: z.literal('session.start'),
    /** Client-generated UUID; lets the app retry `session.start` idempotently. */
    callId: z.string().uuid(),
    clientId: z.string().nullable(),
    contactIds: z.array(z.string()).default([]),
    /**
     * External attendees (e.g. from the calendar event). When a client is chosen,
     * unknown attendees are added to it as contacts and linked to the meeting.
     */
    attendees: z
        .array(z.object({ email: z.string().email(), name: z.string().max(200).nullable().default(null) }))
        .max(50)
        .default([]),
    calendarEventId: z.string().nullable().optional(),
    title: z.string().max(300).default('Sales call'),
    platform: PlatformSchema.default('other'),
    language: z.string().default('en'),
    preCallNotes: z.string().max(4000).default(''),
    /** Must be true: "I've informed the client that I use an AI assistant." */
    consentConfirmed: z.literal(true),
});
export const ClientMessageSchema = z.discriminatedUnion('type', [
    SessionStartSchema,
    z.object({ type: z.literal('session.resume'), meetingId: z.string() }),
    z.object({ type: z.literal('segment'), segment: SegmentPayloadSchema }),
    z.object({ type: z.literal('segments'), segments: z.array(SegmentPayloadSchema).max(500) }),
    z.object({ type: z.literal('suggest.now') }),
    z.object({ type: z.literal('ask'), askId: z.string(), question: z.string().min(1).max(1000) }),
    z.object({ type: z.literal('pause') }),
    z.object({ type: z.literal('resume') }),
    z.object({ type: z.literal('tip.feedback'), tipId: z.string(), useful: z.boolean() }),
    z.object({ type: z.literal('session.end') }),
    z.object({ type: z.literal('ping') }),
]);
export const UsageStateSchema = z.object({
    monthSpendUsd: z.number(),
    limitUsd: z.number(),
    level: z.enum(['ok', 'soft', 'hard']),
});
export const ServerMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('session.started'),
        meetingId: z.string(),
        /** Epoch ms the call started (server clock); used to compute callOffsetMs. */
        startedAt: z.string(),
        brief: ClientBriefSchema.nullable(),
        usage: UsageStateSchema,
        /** Highest segment seq the server already holds (for resume). */
        lastSeq: z.number().int(),
    }),
    z.object({ type: z.literal('ack'), upToSeq: z.number().int() }),
    z.object({ type: z.literal('tips'), trigger: TriggerSchema, tips: z.array(TipSchema) }),
    z.object({ type: z.literal('answer'), askId: z.string(), tip: TipSchema }),
    z.object({ type: z.literal('usage'), usage: UsageStateSchema, message: z.string() }),
    z.object({ type: z.literal('status'), thinking: z.boolean() }),
    z.object({ type: z.literal('session.ended'), meetingId: z.string() }),
    z.object({ type: z.literal('postcall.ready'), meetingId: z.string() }),
    z.object({ type: z.literal('postcall.failed'), meetingId: z.string(), message: z.string() }),
    z.object({
        type: z.literal('error'),
        code: z.string(),
        message: z.string(),
    }),
    z.object({ type: z.literal('pong') }),
]);
/** Cloud STT WebSocket (`/v1/stt`): binary frames of PCM16 LE mono 16 kHz, tagged by source. */
export const STT_SOURCE_BYTE = { me: 0, them: 1 };
export const SttServerMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('segment'),
        source: SourceSchema,
        text: z.string(),
        startMs: z.number(),
        endMs: z.number(),
        final: z.boolean(),
    }),
    z.object({ type: z.literal('error'), message: z.string() }),
]);
//# sourceMappingURL=live.js.map